import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { getUserKey } from '../utils/userStorage';
import { FiColumns, FiFileText, FiFolder, FiSearch } from 'react-icons/fi';

const GlobalSearch = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ projects: [], posts: [], cards: [] });
  const navigate = useNavigate();
  const { t } = useI18n();
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!query.trim()) { setResults({ projects: [], posts: [], cards: [] }); return; }
    const q = query.toLowerCase();

    try {
      const projects = JSON.parse(localStorage.getItem(getUserKey('projects')) || '[]');
      const matchedProjects = projects.filter(p => !p.isArchived && (p.name || '').toLowerCase().includes(q));
      const matchedPosts = [];
      projects.forEach(proj => {
        if (!proj.isArchived) {
          (proj.posts || []).forEach(post => {
            if (!post.isArchived && ((post.title || '').toLowerCase().includes(q) || (post.idea || '').toLowerCase().includes(q))) {
              matchedPosts.push({ ...post, projectName: proj.name });
            }
          });
        }
      });

      const tasks = JSON.parse(localStorage.getItem(getUserKey('kanban')) || '{}');
      const matchedCards = [];
      const boardLabels = { dizayn: 'DIZAYN', montaj: 'MONTAJ', 'vfx-ai': 'VFX & AI' };
      Object.entries(tasks).forEach(([boardId, cols]) => {
        Object.entries(cols || {}).forEach(([colName, cards]) => {
          (cards || []).forEach(card => {
            if ((card.text || '').toLowerCase().includes(q) || (card.description || '').toLowerCase().includes(q)) {
              matchedCards.push({ ...card, boardId, boardName: boardLabels[boardId] || boardId, colName });
            }
          });
        });
      });

      setResults({
        projects: matchedProjects.slice(0, 5),
        posts: matchedPosts.slice(0, 5),
        cards: matchedCards.slice(0, 5),
      });
    } catch {
      setResults({ projects: [], posts: [], cards: [] });
    }
  }, [query]);

  const totalResults = results.projects.length + results.posts.length + results.cards.length;

  const goTo = (path) => { navigate(path); onClose(); };

  const iconBox = (Icon, color) => (
    <div style={{ width: '30px', height: '30px', borderRadius: '7px', background: `${color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>
      <Icon size={15} strokeWidth={1.8} aria-hidden="true" />
    </div>
  );

  const ResultItem = ({ onClick, icon: Icon, iconColor, title, subtitle }) => (
    <div
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 20px', cursor: 'pointer', transition: 'background 0.12s' }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      {iconBox(Icon, iconColor)}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#f1f5f9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
        <div style={{ fontSize: '11px', color: '#6b7280' }}>{subtitle}</div>
      </div>
    </div>
  );

  const SectionHeader = ({ icon: Icon, label }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '10px 20px 4px', fontSize: '10px', fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '1px' }}>
      <Icon size={13} strokeWidth={1.8} aria-hidden="true" /> {label}
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '70px', padding: '70px 16px 0' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)' }} />
      <motion.div
        initial={{ opacity: 0, y: -16, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.97 }}
        transition={{ duration: 0.18 }}
        style={{ position: 'relative', width: '580px', maxWidth: '100%', background: '#111827', borderRadius: '16px', boxShadow: '0 30px 80px rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden' }}
      >
        {/* Search input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <FiSearch size={16} strokeWidth={1.8} color="#6b7280" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('search.placeholder')}
            onKeyDown={e => { if (e.key === 'Escape') onClose(); }}
            style={{ flex: 1, background: 'none', border: 'none', outline: 'none', fontSize: '15px', color: '#f1f5f9', fontFamily: 'inherit' }}
          />
          <kbd style={{ padding: '2px 6px', background: 'rgba(255,255,255,0.07)', borderRadius: '4px', fontSize: '10px', color: '#6b7280', border: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>Esc</kbd>
        </div>

        {/* Results area */}
        <div style={{ maxHeight: '460px', overflowY: 'auto' }}>
          {!query.trim() ? (
            <div style={{ padding: '30px', textAlign: 'center', color: '#4b5563', fontSize: '13px' }}>
              <FiSearch size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} aria-hidden="true" /> {t('search.typing')}
            </div>
          ) : totalResults === 0 ? (
            <div style={{ padding: '30px', textAlign: 'center', color: '#4b5563', fontSize: '13px' }}>
              {t('search.noResults')}
            </div>
          ) : (
            <>
              {results.projects.length > 0 && (
                <div>
                  <SectionHeader icon={FiFolder} label={t('search.projects')} />
                  {results.projects.map(p => (
                    <ResultItem key={p.id} onClick={() => goTo('/projects')} icon={FiFolder} iconColor="#3b82f6" title={p.name} subtitle={`${p.phone || ''} · ${p.targetBudget || ''}`} />
                  ))}
                </div>
              )}
              {results.posts.length > 0 && (
                <div>
                  <SectionHeader icon={FiFileText} label={t('search.contents')} />
                  {results.posts.map((p, i) => (
                    <ResultItem key={i} onClick={() => goTo('/projects')} icon={FiFileText} iconColor="#22c55e" title={p.title} subtitle={`${p.projectName} · ${p.date} · ${p.format}`} />
                  ))}
                </div>
              )}
              {results.cards.length > 0 && (
                <div>
                  <SectionHeader icon={FiColumns} label={t('search.cards')} />
                  {results.cards.map((c, i) => (
                    <ResultItem key={i} onClick={() => goTo('/analytics')} icon={FiColumns} iconColor="#a855f7" title={c.text} subtitle={`${c.boardName} · ${c.colName}`} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer hints */}
        <div style={{ padding: '8px 18px', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: '14px', fontSize: '10px', color: '#4b5563' }}>
          <span><kbd style={{ padding: '1px 4px', background: 'rgba(255,255,255,0.07)', borderRadius: '3px', border: '1px solid rgba(255,255,255,0.1)' }}>↵</kbd> ochish</span>
          <span><kbd style={{ padding: '1px 4px', background: 'rgba(255,255,255,0.07)', borderRadius: '3px', border: '1px solid rgba(255,255,255,0.1)' }}>Esc</kbd> yopish</span>
          <span><kbd style={{ padding: '1px 4px', background: 'rgba(255,255,255,0.07)', borderRadius: '3px', border: '1px solid rgba(255,255,255,0.1)' }}>Ctrl K</kbd> qidirish</span>
        </div>
      </motion.div>
    </div>
  );
};

export default GlobalSearch;
